/** Standard-weight system keys (WsMG = metric, WsIP = English). */
export const STANDARD_WEIGHT_SYSTEMS = {
  METRIC: 'metric',
  ENGLISH: 'english',
};

export const STANDARD_WEIGHT_SYSTEM_OPTIONS = [
  { value: STANDARD_WEIGHT_SYSTEMS.METRIC, label: 'Metric Standard Weight' },
  { value: STANDARD_WEIGHT_SYSTEMS.ENGLISH, label: 'English Standard Weight' },
];

function unitId(unit) {
  return Number(unit?.id ?? unit?.ID);
}

function unitName(unit) {
  return String(unit?.Name ?? unit?.name ?? '').trim();
}

function normalizeName(name) {
  return String(name || '').trim().toLowerCase();
}

function findUnitByNameMatch(units, matcher) {
  const list = Array.isArray(units) ? units : [];
  return list.find((u) => matcher(normalizeName(unitName(u))));
}

export function findMetricLengthUnit(lengthUnits) {
  return findUnitByNameMatch(
    lengthUnits,
    (n) => n === 'mm' || n.includes('millimeter')
  );
}

export function findEnglishLengthUnit(lengthUnits) {
  return findUnitByNameMatch(
    lengthUnits,
    (n) => n.includes('inch') || n === 'in'
  );
}

export function findMetricWeightUnit(weightUnits) {
  return findUnitByNameMatch(
    weightUnits,
    (n) =>
      n === 'gram' ||
      n === 'g' ||
      (n.includes('gram') && !n.includes('kilogram'))
  );
}

export function findEnglishWeightUnit(weightUnits) {
  return findUnitByNameMatch(
    weightUnits,
    (n) => n.includes('pound') || n === 'lb' || n === 'lbs'
  );
}

/**
 * Resolve length/weight unit IDs for the chosen standard-weight system.
 * @returns {null | { lengthUnitId, weightUnitId, lengthUnitLabel, weightUnitLabel, system }}
 */
export function resolveStandardWeightUnits(system, lengthUnits, weightUnits) {
  const isMetric = system === STANDARD_WEIGHT_SYSTEMS.METRIC;
  const lengthUnit = isMetric
    ? findMetricLengthUnit(lengthUnits)
    : findEnglishLengthUnit(lengthUnits);
  const weightUnit = isMetric
    ? findMetricWeightUnit(weightUnits)
    : findEnglishWeightUnit(weightUnits);

  if (!lengthUnit || !weightUnit) {
    return null;
  }

  return {
    lengthUnitId: unitId(lengthUnit),
    weightUnitId: unitId(weightUnit),
    lengthUnitLabel: unitName(lengthUnit),
    weightUnitLabel: unitName(weightUnit),
    system,
  };
}

/** Infer system from saved unit IDs (for edit forms). */
export function inferStandardWeightSystem(
  lengthUnitId,
  weightUnitId,
  lengthUnits,
  weightUnits
) {
  if (!lengthUnitId || !weightUnitId) return '';

  const len = (lengthUnits || []).find((u) => unitId(u) === Number(lengthUnitId));
  const wgt = (weightUnits || []).find((u) => unitId(u) === Number(weightUnitId));
  const ln = normalizeName(unitName(len));
  const wn = normalizeName(unitName(wgt));

  if (ln === 'mm' || wn === 'gram' || wn === 'g') {
    return STANDARD_WEIGHT_SYSTEMS.METRIC;
  }
  if (ln.includes('inch') || wn.includes('pound') || wn === 'lb') {
    return STANDARD_WEIGHT_SYSTEMS.ENGLISH;
  }

  const metricResolved = resolveStandardWeightUnits(
    STANDARD_WEIGHT_SYSTEMS.METRIC,
    lengthUnits,
    weightUnits
  );
  if (
    metricResolved &&
    metricResolved.lengthUnitId === Number(lengthUnitId) &&
    metricResolved.weightUnitId === Number(weightUnitId)
  ) {
    return STANDARD_WEIGHT_SYSTEMS.METRIC;
  }

  const englishResolved = resolveStandardWeightUnits(
    STANDARD_WEIGHT_SYSTEMS.ENGLISH,
    lengthUnits,
    weightUnits
  );
  if (
    englishResolved &&
    englishResolved.lengthUnitId === Number(lengthUnitId) &&
    englishResolved.weightUnitId === Number(weightUnitId)
  ) {
    return STANDARD_WEIGHT_SYSTEMS.ENGLISH;
  }

  return '';
}

export function lookupUnitLabel(units, unitIdValue) {
  if (unitIdValue === '' || unitIdValue == null) return '';
  const unit = (units || []).find((u) => unitId(u) === Number(unitIdValue));
  return unit ? unitName(unit) : '';
}
